import { useContext, useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { CareComponentConfigurationContext } from "@/Flowsheets/Models/CareComponentConfigurationContext";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { FrequencyCareElementConfig } from "@/Flowsheets/Models/CareElements/FrequencyCareElementConfig";
import { LabResultsCareElementConfig } from "@/Flowsheets/Models/CareElements/LabResultsCareElementConfig";
import { ReviewedCareElementConfig } from "@/Flowsheets/Models/CareElements/ReviewedCareElementConfig";
import { VitalSignsCareElementConfig } from "@/Flowsheets/Models/CareElements/VitalSignsCareElementConfig";
import { BooleanDropdown, FrequencyTypeDropdown, OperatorTypeDropdown } from "./CareElementsConfigurationParts/CareElementEnumDropdown";
import { CareElementSimpleTargetInput, CareElementTargetInput, VitalSignTargetInput } from "./CareElementsConfigurationParts/CareElementTargetInput";
import { CareElementConfigColumns, CareElementsConfigurationGrid } from "./CareElementsConfigurationGrid";

function CareElementsConfiguration(): JSX.Element {
    const { getText } = useTranslation();
    const { careComponentName } = useContext(CareComponentConfigurationContext);

    function isVitalSignsConfigurable(elementName: string): boolean {
        return elementName === FlowsheetConstants.VitalSigns.HeightCareElementName;
    }

    const asthmaBasicCareElementsWithConfigurableOperator: string[] = [
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.DyspneaCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.CoughCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.WheezeCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.ChestTightnessCareElementName,
        FlowsheetConstants.AsthmaBasic.Monitoring.RelieverUseCareElementName
    ];

    const asthmaBasicCareElementsWithConfigurableTarget: string[] = [
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.DyspneaCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.CoughCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.WheezeCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.ChestTightnessCareElementName,
        FlowsheetConstants.AsthmaBasic.Monitoring.RelieverUseCareElementName
    ];

    const asthmaBasicCareElementsWithDisabledTarget: string[] = [
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.NightTimeSymptomsCareElementName,
        FlowsheetConstants.AsthmaBasic.Exacerbations.ExacerbationsCareElementName,
        FlowsheetConstants.AsthmaBasic.Monitoring.NumberOfSchoolWorkAbsenceCareElementName
    ];

    const heartFailureBasicCareElementsWithConfigurableOperator: string[] = [
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.FatiguePerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DizzinessOrSyncopePerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DyspneaOnExertionPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DyspneaAtRestPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.OrthopneaPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.ParoxysmalNocturnalDyspneaPerWeekCareElementName
    ];

    const careComponentColumnMapper = useMemo<Record<string, CareElementConfigColumns[]>>(() => ({
        [FlowsheetConstants.LabResults.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: LabResultsCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`labresult-frequency-${element.StandardCode}`} element={element} />
                )
            },
            {
                label: getText("OperatorLabel"),
                getKey: (id: string) => `operator-${id}`,
                render: (element: LabResultsCareElementConfig) => (
                    <OperatorTypeDropdown dataCy={`labresult-operator-${element.StandardCode}`} element={element} />
                )
            },
            {
                label: getText("TargetLabel"),
                getKey: (id: string) => `target-${id}`,
                render: (element: LabResultsCareElementConfig) => (
                    <CareElementTargetInput
                        dataCy={`labresult-target-${element.StandardCode}`}
                        element={element}
                    />
                )
            }
        ],
        [FlowsheetConstants.VitalSigns.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: VitalSignsCareElementConfig) =>
                    (isVitalSignsConfigurable(element.Name)
                        ? <div />
                        : <FrequencyTypeDropdown dataCy={`vitalsign-frequency-${element.Name}`} element={element} />)
            },
            {
                label: getText("OperatorLabel"),
                getKey: (id: string) => `operator-${id}`,
                render: (element: VitalSignsCareElementConfig) =>
                    (isVitalSignsConfigurable(element.Name)
                        ? <div />
                        : <OperatorTypeDropdown dataCy={`vitalsign-operator-${element.Name}`} element={element} />)
            },
            {
                label: getText("TargetLabel"),
                getKey: (id: string) => `target-${id}`,
                render: (element: VitalSignsCareElementConfig) =>
                    (isVitalSignsConfigurable(element.Name)
                        ? <div />
                        : <VitalSignTargetInput dataCy={`vitalsign-target-${element.Name}`} element={element} />)
            }
        ],
        [FlowsheetConstants.RiskFactors.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: FrequencyCareElementConfig) => (
                    element.Name === FlowsheetConstants.RiskFactors.ErectileFunctionCareElementName
                        ? <FrequencyTypeDropdown dataCy={`riskfactor-frequency-${element.Name}`} element={element} />
                        : <div />

                )
            }
        ],
        [FlowsheetConstants.Immunizations.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: FrequencyCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`immunization-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.SelfManagementPlan.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`selfManagement-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.HypertensionBasic.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`hypertension-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.CounselingAndEducation.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`counseling-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.DiabetesBasic.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`diabetes-frequency-${element.Name}`} element={element} />
                )
            },
            {
                label: getText("TargetLabel"),
                getKey: (id: string) => `target-${id}`,
                render: (element: ReviewedCareElementConfig) => {
                    if (element.Name === FlowsheetConstants.DiabetesBasic.GlucoseMonitoring.SelfMonitoringBloodGlucoseCareElementName && element.CareElementType === CareElementType.BooleanReviewed) {
                        return <BooleanDropdown dataCy={`diabetes-target-${element.Name}`} element={element} />;
                    }
                    else if (element.Name === FlowsheetConstants.DiabetesBasic.GlucoseMonitoring.NumberOfHypoglycemicEpisodesCareElementName && element.CareElementType === CareElementType.IntegerReviewed) {
                        return <CareElementSimpleTargetInput disabled dataCy={`diabetes-target-${element.Name}`} element={element} type="integer" />;
                    }
                    return <div />;
                }
            }
        ],
        [FlowsheetConstants.AsthmaBasic.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`asthma-frequency-${element.Name}`} element={element} />
                )
            },
            {
                label: getText("OperatorLabel"),
                getKey: (id: string) => `operator-${id}`,
                render: (element: ReviewedCareElementConfig) => {
                    if (asthmaBasicCareElementsWithConfigurableOperator.contains(element.Name)) {
                        return <OperatorTypeDropdown dataCy={`asthma-operator-${element.Name}`} element={element} />;
                    }
                    return <div />;
                }
            },
            {
                label: getText("TargetLabel"),
                getKey: (id: string) => `target-${id}`,
                render: (element: ReviewedCareElementConfig) => {
                    if (asthmaBasicCareElementsWithConfigurableTarget.contains(element.Name)) {
                        return <CareElementSimpleTargetInput dataCy={`asthma-target-${element.Name}`} element={element} type="integer" />;
                    }
                    else if (asthmaBasicCareElementsWithDisabledTarget.contains(element.Name)) {
                        return <CareElementSimpleTargetInput disabled dataCy={`asthma-target-${element.Name}`} element={element} type="integer" />;
                    }
                    return <div />;
                }
            }
        ],
        [FlowsheetConstants.Screening.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: FrequencyCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`screening-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.ChronicObstructivePulmonaryDiseaseBasic.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`copd-frequency-${element.Name}`} element={element} />
                )
            }
        ],
        [FlowsheetConstants.HeartFailureBasic.CareComponentName]: [
            {
                label: getText("FrequencyLabel"),
                getKey: (id: string) => `frequency-${id}`,
                render: (element: ReviewedCareElementConfig) => (
                    <FrequencyTypeDropdown dataCy={`heart-failure-frequency-${element.Name}`} element={element} />
                )
            },
            {
                label: getText("OperatorLabel"),
                getKey: (id: string) => `operator-${id}`,
                render: (element: ReviewedCareElementConfig) => {
                    if (heartFailureBasicCareElementsWithConfigurableOperator.contains(element.Name)) {
                        return <OperatorTypeDropdown dataCy={`heart-failure-operator-${element.Name}`} element={element} />;
                    }
                    return <div />;
                }
            },
            {
                label: getText("TargetLabel"),
                getKey: (id: string) => `target-${id}`,
                render: (element: ReviewedCareElementConfig) => {
                    if (heartFailureBasicCareElementsWithConfigurableOperator.contains(element.Name)) {
                        return <CareElementSimpleTargetInput dataCy={`heart-failure-target-${element.Name}`} element={element} type="integer" />;
                    }
                    else if (element.Name === FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.NumberOfERVisitsSinceLastAssessmentCareElementName) {
                        return <CareElementSimpleTargetInput disabled dataCy={`heart-failure-target-${element.Name}`} element={element} type="integer" />;
                    }
                    return <div />;
                }
            }
        ]
    }), []);

    return (
        <CareElementsConfigurationGrid careElementsColumns={careComponentColumnMapper[careComponentName]} />
    );
}

export { CareElementsConfiguration };
